import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Printer, FileText, Users, Award, Loader2, 
  Hash, Building2, Check, Settings
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Grade conversion based on AA score
const getGrade = (aaScore) => {
  if (aaScore >= 60.00) return 'Working Beyond';
  if (aaScore >= 55.00) return 'Working Above';
  if (aaScore >= 50.00) return 'Working At';
  return 'Working Towards';
};

// Format date with ordinal suffix
const formatCertificateDate = (date) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  
  const d = new Date(date);
  const dayName = days[d.getDay()];
  const day = d.getDate();
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  
  const getOrdinal = (n) => {
    if (n >= 11 && n <= 13) return 'th';
    switch (n % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  return `${dayName}, ${day}${getOrdinal(day)} ${month} ${year}`;
};

const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

export default function CertificatePrintingPage() {
  const [pinEntered, setPinEntered] = useState(false);
  const [pin, setPin] = useState('');
  const [activeTab, setActiveTab] = useState('certificates');
  
  // Competition state
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [selectedCompetitionData, setSelectedCompetitionData] = useState(null);
  
  // Certificate state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [gymnasts, setGymnasts] = useState([]);
  const [selectedGymnasts, setSelectedGymnasts] = useState([]);
  const [loadingGymnasts, setLoadingGymnasts] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [certificateDate, setCertificateDate] = useState(getTodayDate());
  
  // Gymnast Number Printing State
  const [numberCompetition, setNumberCompetition] = useState('');
  const [numberPrintMode, setNumberPrintMode] = useState('individual');
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [numberGymnasts, setNumberGymnasts] = useState([]);
  const [selectedNumberGymnasts, setSelectedNumberGymnasts] = useState([]);
  const [numberLoading, setNumberLoading] = useState(false);
  const [numberPrinting, setNumberPrinting] = useState(false);

  // Certificate Position Settings (in cm) - GLOBAL for all certificates
  // Each field has: top (from top), bottom (from bottom), left (from left), right (from right)
  // Text baseline sits ON the line position
  const [positionSettings, setPositionSettings] = useState({
    name: { top: null, bottom: 17.5, left: 1, right: 1 },      // Line position for name
    grade: { top: null, bottom: 14.5, left: 1, right: 1 },     // Line position for grade
    date: { top: null, bottom: 11.5, left: 1, right: 1 },      // Line position for date
  });
  const [savingPositions, setSavingPositions] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Load global certificate settings when competition changes
  useEffect(() => {
    if (selectedCompetition && activeTab === 'settings') {
      loadGlobalSettings();
    }
  }, [selectedCompetition, activeTab]);

  const loadGlobalSettings = async () => {
    if (!selectedCompetition) return;
    setLoadingSettings(true);
    try {
      const response = await axios.get(`${API}/printing/certificate-settings/${selectedCompetition}`);
      if (response.data && response.data.settings) {
        setPositionSettings(response.data.settings);
      }
    } catch (error) {
      // Settings don't exist yet, use defaults
      console.log('No existing settings, using defaults');
    } finally {
      setLoadingSettings(false);
    }
  };

  // PIN submission
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pin === '2507') {
      setPinEntered(true);
      toast.success('Certificate Printing System Active');
      loadCompetitions();
    } else {
      toast.error('Invalid PIN');
      setPin('');
    }
  };

  // Load competitions
  const loadCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/music/competitions`);
      setCompetitions(response.data.competitions || []);
    } catch (error) {
      console.error('Error loading competitions:', error);
      toast.error('Failed to load competitions');
    }
  };

  // Load categories when competition selected
  useEffect(() => {
    if (selectedCompetition) {
      const comp = competitions.find(c => c.id === selectedCompetition);
      if (comp) {
        setSelectedCompetitionData(comp);
        loadCategories(selectedCompetition);
      }
    } else {
      setCategories([]);
      setSelectedCategory('');
      setGymnasts([]);
    }
  }, [selectedCompetition, competitions]);

  const loadCategories = async (compId) => {
    try {
      const response = await axios.get(`${API}/printing/categories/${compId}`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    }
  };

  // Load gymnasts when category selected
  useEffect(() => {
    if (selectedCompetition && selectedCategory) {
      loadGymnasts();
    } else {
      setGymnasts([]);
      setSelectedGymnasts([]);
    }
  }, [selectedCategory]);

  const loadGymnasts = async () => {
    setLoadingGymnasts(true);
    try {
      const response = await axios.get(
        `${API}/printing/gymnasts/${selectedCompetition}?category=${encodeURIComponent(selectedCategory)}`
      );
      setGymnasts(response.data.gymnasts || []);
      setSelectedGymnasts([]);
    } catch (error) {
      console.error('Error loading gymnasts:', error);
      toast.error('Failed to load gymnasts');
    } finally {
      setLoadingGymnasts(false);
    }
  };

  const toggleGymnastSelection = (gymnastId) => {
    setSelectedGymnasts(prev => 
      prev.includes(gymnastId)
        ? prev.filter(id => id !== gymnastId)
        : [...prev, gymnastId]
    );
  };

  const selectAllGymnasts = () => {
    if (selectedGymnasts.length === gymnasts.length) {
      setSelectedGymnasts([]);
    } else {
      setSelectedGymnasts(gymnasts.map(g => g.id));
    }
  };

  // Print certificates (simple text-based)
  const handlePrintCertificates = async () => {
    if (selectedGymnasts.length === 0) {
      toast.error('Please select at least one gymnast');
      return;
    }

    setPrinting(true);
    try {
      const formattedDate = formatCertificateDate(certificateDate);
      const selectedGymnastData = gymnasts.filter(g => selectedGymnasts.includes(g.id));
      
      const certificateData = selectedGymnastData.map(g => ({
        fullName: g.fullName,
        grade: getGrade(g.aaScore),
        date: formattedDate,
        aaScore: g.aaScore
      }));

      const response = await axios.post(`${API}/printing/generate-certificates`, {
        competitionId: selectedCompetition,
        category: selectedCategory,
        certificates: certificateData
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificates_${selectedCategory.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Generated ${certificateData.length} certificate(s)`);
    } catch (error) {
      console.error('Error generating certificates:', error);
      toast.error('Failed to generate certificates');
    } finally {
      setPrinting(false);
    }
  };

  // ========== GYMNAST NUMBERS FUNCTIONS ==========
  
  const loadClubsForCompetition = async (compId) => {
    try {
      setNumberLoading(true);
      const response = await axios.get(`${API}/printing/clubs/${compId}`);
      setClubs(response.data.clubs || []);
    } catch (error) {
      console.error('Error loading clubs:', error);
      toast.error('Failed to load clubs');
    } finally {
      setNumberLoading(false);
    }
  };

  const loadGymnastsForNumbers = async (compId, clubId = null) => {
    try {
      setNumberLoading(true);
      const url = clubId 
        ? `${API}/printing/gymnasts-numbers/${compId}?clubId=${clubId}`
        : `${API}/printing/gymnasts-numbers/${compId}`;
      const response = await axios.get(url);
      setNumberGymnasts(response.data.gymnasts || []);
      setSelectedNumberGymnasts([]);
    } catch (error) {
      console.error('Error loading gymnasts:', error);
      toast.error('Failed to load gymnasts');
    } finally {
      setNumberLoading(false);
    }
  };

  useEffect(() => {
    if (numberCompetition) {
      loadClubsForCompetition(numberCompetition);
      if (numberPrintMode === 'individual') {
        loadGymnastsForNumbers(numberCompetition);
      }
    }
  }, [numberCompetition]);

  useEffect(() => {
    if (selectedClub && numberPrintMode === 'club') {
      loadGymnastsForNumbers(numberCompetition, selectedClub);
    }
  }, [selectedClub]);

  const toggleNumberGymnastSelection = (gymnastId) => {
    setSelectedNumberGymnasts(prev => 
      prev.includes(gymnastId)
        ? prev.filter(id => id !== gymnastId)
        : [...prev, gymnastId]
    );
  };

  const selectAllNumberGymnasts = () => {
    if (selectedNumberGymnasts.length === numberGymnasts.length) {
      setSelectedNumberGymnasts([]);
    } else {
      setSelectedNumberGymnasts(numberGymnasts.map(g => g.id));
    }
  };

  const printGymnastNumbers = async () => {
    if (selectedNumberGymnasts.length === 0) {
      toast.error('Please select at least one gymnast');
      return;
    }

    setNumberPrinting(true);
    try {
      const response = await axios.post(`${API}/printing/generate-numbers`, {
        competitionId: numberCompetition,
        gymnastIds: selectedNumberGymnasts
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'gymnast_numbers.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Generated ${selectedNumberGymnasts.length} gymnast number(s)`);
    } catch (error) {
      console.error('Error generating gymnast numbers:', error);
      toast.error('Failed to generate gymnast numbers');
    } finally {
      setNumberPrinting(false);
    }
  };

  // ========== RENDER ==========

  if (!pinEntered) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Award className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <CardTitle className="text-2xl">Printing System</CardTitle>
            <CardDescription>Enter PIN to access certificate and number printing</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <Input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter PIN"
                className="text-center text-2xl tracking-widest"
                maxLength={4}
                data-testid="printing-pin-input"
              />
              <Button type="submit" className="w-full" data-testid="printing-pin-submit">
                Access Printing System
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Printing System</h1>
          <p className="text-gray-500">Generate certificates and gymnast numbers</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="certificates" className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              Certificates
            </TabsTrigger>
            <TabsTrigger value="numbers" className="flex items-center gap-2">
              <Hash className="w-4 h-4" />
              Gymnast Numbers
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Position Settings
            </TabsTrigger>
          </TabsList>

          {/* CERTIFICATES TAB - Simple Version */}
          <TabsContent value="certificates" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Print Certificates</CardTitle>
                <CardDescription>
                  Select gymnasts to print their Name, Achievement (Grade), and Date
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Competition Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Competition</Label>
                  <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
                    <SelectTrigger data-testid="cert-competition-select">
                      <SelectValue placeholder="Select competition" />
                    </SelectTrigger>
                    <SelectContent>
                      {competitions.map(comp => (
                        <SelectItem key={comp.id} value={comp.id}>
                          {comp.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Selection */}
                {selectedCompetition && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Category</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger data-testid="cert-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Date Selection */}
                {selectedCategory && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Certificate Date</Label>
                    <Input
                      type="date"
                      value={certificateDate}
                      onChange={(e) => setCertificateDate(e.target.value)}
                      data-testid="cert-date-input"
                    />
                    <p className="text-xs text-gray-500">
                      Will print as: {formatCertificateDate(certificateDate)}
                    </p>
                  </div>
                )}

                {/* Gymnasts List */}
                {loadingGymnasts ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : gymnasts.length > 0 ? (
                  <div className="space-y-4">
                    {/* Select All */}
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectAllGymnasts}
                      >
                        {selectedGymnasts.length === gymnasts.length ? 'Deselect All' : 'Select All'}
                      </Button>
                      <span className="text-sm text-gray-500">
                        {selectedGymnasts.length} of {gymnasts.length} selected
                      </span>
                    </div>

                    {/* Gymnast Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                      {gymnasts.map(gymnast => (
                        <div
                          key={gymnast.id}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedGymnasts.includes(gymnast.id)
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => toggleGymnastSelection(gymnast.id)}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={selectedGymnasts.includes(gymnast.id)}
                              onCheckedChange={() => toggleGymnastSelection(gymnast.id)}
                            />
                            <div className="flex-1">
                              <div className="font-medium">{gymnast.fullName}</div>
                              <div className="text-sm text-gray-500">
                                AA: {gymnast.aaScore?.toFixed(3) || 'N/A'} • {getGrade(gymnast.aaScore)}
                              </div>
                            </div>
                            {selectedGymnasts.includes(gymnast.id) && (
                              <Check className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Print Button */}
                    <Button
                      onClick={handlePrintCertificates}
                      disabled={printing || selectedGymnasts.length === 0}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="print-certificates-btn"
                    >
                      {printing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Printer className="w-4 h-4 mr-2" />
                          Print {selectedGymnasts.length} Certificate{selectedGymnasts.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                  </div>
                ) : selectedCategory ? (
                  <div className="text-center py-12 text-gray-400">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No gymnasts found</p>
                    <p className="text-sm mt-1">No gymnasts with scores in this category</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GYMNAST NUMBERS TAB */}
          <TabsContent value="numbers" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Hash className="w-5 h-5" />
                  Print Gymnast Numbers
                </CardTitle>
                <CardDescription>
                  Select gymnasts individually or by club to print their competition numbers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Competition Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Competition</Label>
                  <Select value={numberCompetition} onValueChange={(val) => {
                    setNumberCompetition(val);
                    setSelectedClub('');
                    setNumberGymnasts([]);
                    setSelectedNumberGymnasts([]);
                  }}>
                    <SelectTrigger data-testid="number-competition-select">
                      <SelectValue placeholder="Select competition" />
                    </SelectTrigger>
                    <SelectContent>
                      {competitions.map(comp => (
                        <SelectItem key={comp.id} value={comp.id}>
                          {comp.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {numberCompetition && (
                  <>
                    {/* Print Mode Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Print Mode</Label>
                      <div className="flex gap-4">
                        <Button
                          variant={numberPrintMode === 'individual' ? 'default' : 'outline'}
                          onClick={() => {
                            setNumberPrintMode('individual');
                            setSelectedClub('');
                            loadGymnastsForNumbers(numberCompetition);
                          }}
                          className="flex-1"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Individual Gymnasts
                        </Button>
                        <Button
                          variant={numberPrintMode === 'club' ? 'default' : 'outline'}
                          onClick={() => {
                            setNumberPrintMode('club');
                            setNumberGymnasts([]);
                            setSelectedNumberGymnasts([]);
                          }}
                          className="flex-1"
                        >
                          <Building2 className="w-4 h-4 mr-2" />
                          By Club
                        </Button>
                      </div>
                    </div>

                    {/* Club Selection (when in club mode) */}
                    {numberPrintMode === 'club' && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Select Club</Label>
                        <Select value={selectedClub} onValueChange={setSelectedClub}>
                          <SelectTrigger data-testid="number-club-select">
                            <SelectValue placeholder="Select a club" />
                          </SelectTrigger>
                          <SelectContent>
                            {clubs.map(club => (
                              <SelectItem key={club.id} value={club.id}>
                                {club.name} ({club.gymnastCount} gymnasts)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Gymnasts List */}
                    {numberLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                      </div>
                    ) : numberGymnasts.length > 0 ? (
                      <div className="space-y-4">
                        {/* Select All / Actions */}
                        <div className="flex items-center justify-between">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={selectAllNumberGymnasts}
                          >
                            {selectedNumberGymnasts.length === numberGymnasts.length ? 'Deselect All' : 'Select All'}
                          </Button>
                          <span className="text-sm text-gray-500">
                            {selectedNumberGymnasts.length} of {numberGymnasts.length} selected
                          </span>
                        </div>

                        {/* Gymnast Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                          {numberGymnasts.map(gymnast => (
                            <div
                              key={gymnast.id}
                              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                selectedNumberGymnasts.includes(gymnast.id)
                                  ? 'border-green-500 bg-green-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              onClick={() => toggleNumberGymnastSelection(gymnast.id)}
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={selectedNumberGymnasts.includes(gymnast.id)}
                                  onCheckedChange={() => toggleNumberGymnastSelection(gymnast.id)}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono font-bold text-lg">
                                      {gymnast.number}{gymnast.letter || ''}
                                    </span>
                                    <span className="font-medium">{gymnast.name}</span>
                                  </div>
                                  <div className="text-sm text-gray-500">{gymnast.clubName}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Print Button */}
                        <Button
                          onClick={printGymnastNumbers}
                          disabled={numberPrinting || selectedNumberGymnasts.length === 0}
                          className="w-full bg-green-600 hover:bg-green-700"
                          data-testid="print-numbers-btn"
                        >
                          {numberPrinting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Printer className="w-4 h-4 mr-2" />
                              Print {selectedNumberGymnasts.length} Gymnast Number{selectedNumberGymnasts.length !== 1 ? 's' : ''}
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <Hash className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-lg">No gymnasts loaded</p>
                        <p className="text-sm mt-1">
                          {numberPrintMode === 'club' 
                            ? 'Select a club to load their gymnasts'
                            : 'Select a competition to load gymnasts'}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SETTINGS TAB - Certificate Position Coordinates */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Certificate Position Settings</CardTitle>
                <CardDescription>
                  Configure where text appears on ALL certificates for this competition.
                  Specify the line position - text will print ABOVE the line.
                  <br /><strong>A4 Page: 21.0 cm wide × 29.7 cm tall</strong>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Competition Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Competition</Label>
                  <Select 
                    value={selectedCompetition} 
                    onValueChange={(val) => {
                      setSelectedCompetition(val);
                      const comp = competitions.find(c => c.id === val);
                      setSelectedCompetitionData(comp);
                    }}
                  >
                    <SelectTrigger data-testid="settings-competition-select">
                      <SelectValue placeholder="Select competition" />
                    </SelectTrigger>
                    <SelectContent>
                      {competitions.map((comp) => (
                        <SelectItem key={comp.id} value={comp.id}>{comp.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedCompetition && (
                  <>
                    {loadingSettings ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        <span>Loading settings...</span>
                      </div>
                    ) : (
                      <>
                        {/* NAME Position */}
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <Label className="font-semibold text-gray-800 text-base">NAME Line Position</Label>
                          <p className="text-xs text-gray-500">Where the name line is on the certificate. Text prints above this line.</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From TOP (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Leave empty to use bottom"
                                value={positionSettings.name.top ?? ''}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  name: { ...prev.name, top: e.target.value ? parseFloat(e.target.value) : null }
                                }))}
                                className="text-center"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From BOTTOM (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Leave empty to use top"
                                value={positionSettings.name.bottom ?? ''}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  name: { ...prev.name, bottom: e.target.value ? parseFloat(e.target.value) : null }
                                }))}
                                className="text-center"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From LEFT (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={positionSettings.name.left ?? 1}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  name: { ...prev.name, left: parseFloat(e.target.value) || 0 }
                                }))}
                                className="text-center"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From RIGHT (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={positionSettings.name.right ?? 1}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  name: { ...prev.name, right: parseFloat(e.target.value) || 0 }
                                }))}
                                className="text-center"
                              />
                            </div>
                          </div>
                        </div>

                        {/* GRADE Position */}
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <Label className="font-semibold text-gray-800 text-base">GRADE Line Position</Label>
                          <p className="text-xs text-gray-500">Where the grade/achievement line is. Text prints above this line.</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From TOP (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Leave empty to use bottom"
                                value={positionSettings.grade.top ?? ''}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  grade: { ...prev.grade, top: e.target.value ? parseFloat(e.target.value) : null }
                                }))}
                                className="text-center"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From BOTTOM (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Leave empty to use top"
                                value={positionSettings.grade.bottom ?? ''}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  grade: { ...prev.grade, bottom: e.target.value ? parseFloat(e.target.value) : null }
                                }))}
                                className="text-center"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From LEFT (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={positionSettings.grade.left ?? 1}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  grade: { ...prev.grade, left: parseFloat(e.target.value) || 0 }
                                }))}
                                className="text-center"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From RIGHT (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={positionSettings.grade.right ?? 1}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  grade: { ...prev.grade, right: parseFloat(e.target.value) || 0 }
                                }))}
                                className="text-center"
                              />
                            </div>
                          </div>
                        </div>

                        {/* DATE Position */}
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <Label className="font-semibold text-gray-800 text-base">DATE Line Position</Label>
                          <p className="text-xs text-gray-500">Where the date line is. Text prints above this line.</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From TOP (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Leave empty to use bottom"
                                value={positionSettings.date.top ?? ''}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  date: { ...prev.date, top: e.target.value ? parseFloat(e.target.value) : null }
                                }))}
                                className="text-center"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From BOTTOM (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Leave empty to use top"
                                value={positionSettings.date.bottom ?? ''}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  date: { ...prev.date, bottom: e.target.value ? parseFloat(e.target.value) : null }
                                }))}
                                className="text-center"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From LEFT (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={positionSettings.date.left ?? 1}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  date: { ...prev.date, left: parseFloat(e.target.value) || 0 }
                                }))}
                                className="text-center"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-500">From RIGHT (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={positionSettings.date.right ?? 1}
                                onChange={(e) => setPositionSettings(prev => ({
                                  ...prev,
                                  date: { ...prev.date, right: parseFloat(e.target.value) || 0 }
                                }))}
                                className="text-center"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Save Button */}
                        <Button
                          onClick={async () => {
                            setSavingPositions(true);
                            try {
                              await axios.put(`${API}/printing/certificate-settings/${selectedCompetition}`, {
                                settings: positionSettings
                              });
                              toast.success('Certificate settings saved for ALL categories');
                            } catch (error) {
                              console.error('Error saving settings:', error);
                              toast.error('Failed to save certificate settings');
                            } finally {
                              setSavingPositions(false);
                            }
                          }}
                          disabled={savingPositions}
                          className="w-full bg-black hover:bg-gray-800"
                        >
                          {savingPositions ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Save Settings (Applies to ALL Certificates)
                            </>
                          )}
                        </Button>

                        {/* Help Text */}
                        <div className="text-xs text-gray-500 space-y-1 mt-4 p-3 bg-blue-50 rounded-lg">
                          <p><strong>A4 Page Size:</strong> 21.0 cm wide × 29.7 cm tall</p>
                          <p><strong>How it works:</strong></p>
                          <ul className="list-disc ml-4 space-y-1">
                            <li>Use TOP or BOTTOM to set vertical position (not both)</li>
                            <li>LEFT and RIGHT define the text area width</li>
                            <li>Text baseline sits ON the line you specify</li>
                            <li>Text is centered between left and right margins</li>
                          </ul>
                        </div>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
